/****************************
 * Special instance of axios
 * that refreshes tokens if
 * the token is about to expire
 * (preemptive) or if a 401
 * is received (reactive fallback)
 ****************************/
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import store from "../store";
import jwtDecode from "jwt-decode";
import { User } from "@/shared/interfaces/user/User.interface";
import { NOT_AUTHORIZED, USER_DATA_ERROR_INVALID_ANONYMOUS_TOKEN } from "./helpers/constants/payload";
import { userHasRole } from "@/shared/scripts/helpers/function";
import { logout } from "@/shared/scripts/Authenticate";
import { buildUserName } from "@/shared/scripts/helpers/user/user";


const autoRefreshAxios = axios.create();

// Shared refresh promise — ensures only one refresh is in flight at a time.
// .finally() guarantees cleanup even on failure (no deadlock).
let refreshPromise: Promise<string> | null = null;

function isTokenExpiredError(errorResponse: AxiosResponse): boolean {
    return errorResponse.status === NOT_AUTHORIZED || errorResponse.data.code === USER_DATA_ERROR_INVALID_ANONYMOUS_TOKEN;
}

function isJwtExpired(token: string, marginSeconds = 60): boolean {
    try {
        const decoded: any = jwtDecode(token);
        return decoded.exp * 1000 - Date.now() < marginSeconds * 1000;
    } catch {
        return true;
    }
}

async function refreshToken(requestConfig?: AxiosRequestConfig): Promise<string> {
    const user = store.getters["UserModule/getUser"];
    if (!user) {
        throw new Error("No user in store");
    }

    let isAdminToken = false;
    let data = {
        "site": "IMMOWEB_ALL"
    };

    if (requestConfig && requestConfig.headers && requestConfig.headers.Authorization === user.jwtAdmin) {
        data = {
            "site": "BACK_OFFICE"
        };
        isAdminToken = true;
    }

    const baseUrl: string = process.env.VUE_APP_PHP_PORTAL_ENDPOINT || "";

    const response = await axios({
        method: "post",
        url: baseUrl + "refresh",
        headers: {
            "Accept": "application/json",
            "x-client-source": "immoweb.funnel"
        },
        data: data,
        withCredentials: true
    }).catch(() => {
        logout();
        store.commit("ModalModule/openLoginModal");
        throw new Error("Refresh failed");
    });

    if (!response || !response.data || !response.data.jwtToken) {
        throw new Error("No jwtToken in refresh response");
    }

    const userData: Record<string, unknown> = jwtDecode(response.data.jwtToken);
    const authorizationsStatement = (userData.authorizations as Record<string, Array<Record<string, string>>>).statements;

    const newUser: User = {
        id: userData.id as string,
        jwtToken: user.jwtToken,
        jwtAdmin: user.jwtAdmin,
        username: buildUserName(userData.firstName, userData.lastName),
        isAnonymous: userHasRole("ANONYMOUS", authorizationsStatement),
        isMasquerading: userData.modeMasquerade as boolean,
        language: userData.language as string
    };

    if (isAdminToken) {
        newUser.jwtAdmin = response.data.jwtToken;
    } else {
        newUser.jwtToken = response.data.jwtToken;
    }

    const userEmail = userData.email;
    if (userEmail) {
        newUser.email = userEmail as string;
    }

    store.commit("UserModule/setUser", newUser);

    return isAdminToken ? newUser.jwtAdmin as string : newUser.jwtToken;
}

function ensureValidToken(requestConfig?: AxiosRequestConfig): Promise<string> {
    if (refreshPromise) {
        return refreshPromise;
    }

    refreshPromise = refreshToken(requestConfig).finally(() => {
        refreshPromise = null;
    });

    return refreshPromise;
}

// REQUEST interceptor — preemptive refresh before sending request
autoRefreshAxios.interceptors.request.use(
    async function(config: AxiosRequestConfig) {
        const user = store.getters["UserModule/getUser"];
        if (!user) {
            return config;
        }

        const currentAuth = config.headers ? config.headers.Authorization : undefined;
        const isUsingUserToken = currentAuth === "Bearer " + user.jwtToken;
        const isUsingAdminToken = !!user.jwtAdmin && currentAuth === "Bearer " + user.jwtAdmin;

        if (isUsingUserToken && isJwtExpired(user.jwtToken)) {
            try {
                const newToken = await ensureValidToken(config);
                config.headers.Authorization = "Bearer " + newToken;
            } catch {
                // Let the request proceed with the old token;
                // the response interceptor will handle the 401.
            }
        } else if (isUsingAdminToken && isJwtExpired(user.jwtAdmin as string)) {
            try {
                const newToken = await ensureValidToken(config);
                config.headers.Authorization = "Bearer " + newToken;
            } catch {
                // Let the request proceed with the old token;
                // the response interceptor will handle the 401.
            }
        }

        return config;
    },
    function(error) {
        return Promise.reject(error);
    }
);

// RESPONSE interceptor — safety net for 401s that slip through
autoRefreshAxios.interceptors.response.use(
    function(response: AxiosResponse) {
        return response;
    },
    async function(error: AxiosError) {
        const errorResponse = error.response;
        if (!errorResponse || !isTokenExpiredError(errorResponse)) {
            return Promise.reject(error);
        }

        const originalConfig = errorResponse.config as AxiosRequestConfig & { _retry?: boolean; skipAuthRefresh?: boolean };
        if (originalConfig._retry || originalConfig.skipAuthRefresh) {
            return Promise.reject(error);
        }
        originalConfig._retry = true;

        try {
            const newToken = await ensureValidToken(originalConfig);

            if (errorResponse.status === NOT_AUTHORIZED) {
                originalConfig.headers.Authorization = "Bearer " + newToken;
            }
            if (errorResponse.data.code === USER_DATA_ERROR_INVALID_ANONYMOUS_TOKEN) {
                originalConfig.headers["anonymous-access-token"] = newToken;
            }

            return autoRefreshAxios(originalConfig);
        } catch (refreshError) {
            return Promise.reject(refreshError);
        }
    }
);

export default autoRefreshAxios;
